import { makeAutoObservable, runInAction } from "mobx";
import * as api from "../api";
import { hasRecurringTimeSlots } from "../utils/timeSlots";

const makeInitialChosenTicketsState = (tickets) => {
    return tickets.reduce((initialState, ticket) => {
        initialState[ticket._id] = { amount: 0, info: {} }
        return initialState
    }, {})
}

class CheckoutStore {
    loading = false;
    activeStep = 0;
    initPromise = null;
    chosenTickets = null;
    initialized = false;
    splitPayment = null;
    prepareSplitPayment = false;
    splitPaymentIframeLink = null;
    splitPaymentError = null;

    constructor() {
        makeAutoObservable(this);
    }

    get totalChosenTickets() {
        if (!this.chosenTickets) return 0;
        return Object.values(this.chosenTickets).reduce((acc, ticket) => acc + ticket.amount, 0);
    }

    init = (event, tickets) => {
        this.event = event;
        this.chosenTickets = makeInitialChosenTicketsState(tickets);
        // Only set initial step if not already initialized or if we're at step 10
        // This prevents resetting when transitioning from time slots to tickets
        if (!this.initialized || (this.activeStep !== 0 && this.activeStep !== 1 && this.activeStep !== 2)) {
            this.activeStep = hasRecurringTimeSlots(event) ? 10 : 0;
        }
        this.initialized = true;
    }

    setChosenTickets = (chosenTickets) => {
        this.chosenTickets = chosenTickets;
    }

    setActiveStep = async (step) => {
        try {
            if (this.activeStep === step) return;
            if (step === 1 || step === 2) {
                await this.checkEventTicketsLeft();
            }
            runInAction(() => {
                this.activeStep = step;
            });
        } catch (error) {
            console.error(error);
        }
    }

    reset = () => {
        this.activeStep = 0;
        this.chosenTickets = null;
        this.initialized = false;
        this.splitPayment = null;
        this.prepareSplitPayment = false;
        this.splitPaymentIframeLink = null;
        this.splitPaymentError = null;
    }

    getSplitPaymentIframeLink = async (data) => {
        this.prepareSplitPayment = true;
        this.splitPaymentError = null;
        try {
            const res = await api.createDivideOrder({ data });
            runInAction(() => {
                this.splitPaymentIframeLink = res.data.link;
            })
            this.setActiveStep(2);
        } catch (error) {
            console.error(error);
            this.splitPaymentError = error;
        } finally {
            runInAction(() => {
                this.prepareSplitPayment = false;
            });
        }
    }

    checkEventTicketsLeft = async () => {
        if (this.event.isSeatsEvent) return true; // no need to check tickets for seating events
        if (this.event.isWaitingListEnabled) return true; // no need to check tickets for waiting list events
        this.loading = true;
        try {
            const { data: tickets } = await api.fetchEventTickets({ accountId: this.event.accountId, eventId: this.event._id });
            if (!tickets.length) {
                return;
            }
            // TODO:
            const { eventCapacityLeft } = tickets[0];
            if (this.event.maxCapacityEnabled && !isNaN(eventCapacityLeft) && eventCapacityLeft <= 0) {
                // no more tickets left
                this.setActiveStep(7);
                throw new Error('no_tickets_left');
            }
            const totalChosenTickets = Object.values(this.chosenTickets).reduce((acc, ticket) => acc + ticket.amount, 0);
            if (this.event.maxCapacityEnabled && !isNaN(eventCapacityLeft) && totalChosenTickets > eventCapacityLeft) {
                // too many tickets chosen
                this.setActiveStep(8);
                throw new Error('too_many_tickets_chosen');
            }
            // validate that there are enough tickets left for the chosen tickets
            // use a for loop to break out of the loop when there are no more tickets left of a certain type
            for (let chosenTicketId in this.chosenTickets) {
                const chosenTicket = this.chosenTickets[chosenTicketId];
                const chosenTicketAmount = chosenTicket.amount;
                if (!chosenTicketAmount) continue;
                const ticket = tickets.find((t) => t._id === chosenTicketId);
                if (!ticket || ticket.is_sold_out) {
                    this.setActiveStep(8);
                    throw new Error('ticket_sold_out');
                }
                // if there are not enough tickets left, set the active step to 7
                const availableTickets = this.event.maxCapacityEnabled ? Math.min(eventCapacityLeft, ticket.ticketsLeft) : ticket.ticketsLeft;
                if (chosenTicketAmount > availableTickets) {
                    this.setActiveStep(8);
                    throw new Error('ticket_sold_out');
                }
            }
        } catch (error) {
            console.error(error);
            throw error;
        } finally {
            runInAction(() => {
                this.loading = false;
            });
        }
    }
}

export default new CheckoutStore();
